// Phase 707 — Reusable Send-for-Approval dialog. Works for ANY
// document_type because the backend module is generic. Pass the
// document context as props.

import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Props:
 *   open, onOpenChange
 *   documentType    e.g. "annual_statement"
 *   documentId      stable id (e.g. `${building_id}_${year}`)
 *   documentLabel   e.g. "Annual Statement 2026"
 *   pdfUrl          relative API path that returns the PDF
 *   pdfFilename     suggested filename
 *   buildingId, apartmentId (optional)
 *   defaultOwnerEmail, defaultOwnerName (optional)
 */
export default function SendForApprovalDialog({
  open, onOpenChange,
  documentType, documentId, documentLabel,
  pdfUrl, pdfFilename,
  buildingId = '', apartmentId = '',
  defaultOwnerEmail = '', defaultOwnerName = '',
  onSent,
}) {
  const [email, setEmail] = useState(defaultOwnerEmail);
  const [name, setName] = useState(defaultOwnerName);
  const [expiresDays, setExpiresDays] = useState(30);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      setEmail(defaultOwnerEmail);
      setName(defaultOwnerName);
      setExpiresDays(30);
    }
  }, [open, defaultOwnerEmail, defaultOwnerName]);

  const submit = async () => {
    if (!email.trim()) { toast.error('Recipient email is required'); return; }
    setBusy(true);
    try {
      const publicBase = window.location.origin;
      const res = await axios.post(`${API}/document-approvals`, {
        document_type: documentType,
        document_id: documentId,
        document_label: documentLabel,
        pdf_url: pdfUrl,
        pdf_filename: pdfFilename,
        building_id: buildingId,
        apartment_id: apartmentId,
        owner_email: email.trim(),
        owner_name: name.trim(),
        expires_days: Number(expiresDays) || 30,
        public_base_url: publicBase,
      });
      const ok = res.data?.email_sent;
      toast.success(ok ? 'Approval request sent.' : 'Approval created — email not sent (check Brevo).');
      onSent?.(res.data?.approval);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send approval request');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-[520px] rounded-none" data-testid="send-for-approval-dialog">
        <DialogHeader>
          <DialogTitle>Send for Approval</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            The recipient gets the PDF attached + a secure review link. They can
            Accept (with electronic signature), Decline, or Request Changes.
            Every action is logged with timestamp + IP.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="text-xs bg-zinc-50 border border-zinc-200 p-2">
            <div className="text-zinc-500 uppercase tracking-wider">Document</div>
            <div className="font-mono text-zinc-800">{documentLabel}</div>
          </div>
          <div>
            <Label className="text-xs">Recipient Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none h-9 mt-1" data-testid="approval-owner-name-input" />
          </div>
          <div>
            <Label className="text-xs">Recipient Email *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none h-9 mt-1 font-mono" data-testid="approval-owner-email-input" />
          </div>
          <div>
            <Label className="text-xs">Link Expires (days)</Label>
            <Input type="number" min={1} max={365} value={expiresDays} onChange={(e) => setExpiresDays(e.target.value)} className="rounded-none h-9 mt-1 font-mono w-32" data-testid="approval-expires-input" />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-zinc-950 rounded-none" onClick={submit} disabled={busy} data-testid="approval-send-btn">{busy ? 'Sending…' : 'Send for Approval'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
